import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Statistic, Table, Progress, Spin, Typography, Tag, List, Avatar, Input, Select, Space, DatePicker, Button, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { TrophyOutlined, TeamOutlined, ShopOutlined, UserOutlined, BookOutlined, SearchOutlined, BankOutlined, ReloadOutlined } from '@ant-design/icons';
import { getCompetitionReports } from '../../../core/competition/competitionApi';
import { RootState, AppDispatch } from '../../../core/store';
import { CompetitionType, CompetitionLevel, SchoolWithStatistics } from '../../../core/competition/types';
import { schoolTypeOptions } from '../../../core/utils/helpers';
import './competitionStatistics.scss';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const { RangePicker } = DatePicker;

const CompetitionStatistics: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { reports, isReportsFetching, date_from, date_to } = useSelector((state: RootState) => state.competition);

    const [selectedSchool, setSelectedSchool] = useState<SchoolWithStatistics | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSchoolType, setSelectedSchoolType] = useState<string>('');
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => {
        const now = dayjs();
        const currentYear = now.year();
        const isOnOrAfterSeptember = now.month() >= 8; // 0-indexed months, 8 = September
        const startYear = isOnOrAfterSeptember ? currentYear : currentYear - 1;
        const endYear = isOnOrAfterSeptember ? currentYear + 1 : currentYear;
        const start = dayjs(`${startYear}-09-01`);
        const end = dayjs(`${endYear}-08-31`);
        return [start, end];
    });

    useEffect(() => {
        dispatch(getCompetitionReports({
            date_from: dateRange[0].format('YYYY-MM-DD'),
            date_to: dateRange[1].format('YYYY-MM-DD'),
        }));
    }, [dispatch, dateRange]);

    const handleDateChange = (dates: any) => {
        if (!dates || dates.length !== 2) {
            return;
        }
        setSelectedSchool(null);
        setDateRange(dates);
    };

    const handleRefresh = () => {
        dispatch(getCompetitionReports({
            date_from: dateRange[0].format('YYYY-MM-DD'),
            date_to: dateRange[1].format('YYYY-MM-DD'),
            force_refresh: true,
        }));
    };

    if (isReportsFetching) {
        return (
            <div className="competition-statistics">
                <div className="loading-container">
                    <Spin size="large" />
                    <div>Kraunama statistika...</div>
                </div>
            </div>
        );
    }

    if (!reports) {
        return (
            <div className="competition-statistics">
                <Card>
                    <div className="error-container">
                        Nepavyko užkrauti statistikos
                    </div>
                </Card>
            </div>
        );
    }

    const { overall_statistics, competition_statistics_by_type, winners_statistics_by_type,
        winners_by_place_level, top_schools, all_schools_with_statistics } = reports;

    // Filter schools based on search and type
    const filteredSchools = all_schools_with_statistics.filter(school => {
        const matchesSearch = school.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = !selectedSchoolType || school.type === selectedSchoolType;
        return matchesSearch && matchesType;
    });

    const getCompetitionTypeLabel = (type: CompetitionType): string => {
        const labels: Record<CompetitionType, string> = {
            municipal_olympiad: 'Savivaldybės olimpiada',
            national_olympiad: 'Nacionalinė olimpiada',
            national_competition: 'Nacionalinis konkursas',
            linesa: 'LINESA',
            other: 'Kita',
        };
        return labels[type];
    };

    const getPlaceLevelLabel = (level: CompetitionLevel): string => {
        const labels: Record<CompetitionLevel, string> = {
            I: 'I vieta',
            II: 'II vieta',
            III: 'III vieta',
            SPECIAL: 'Speciali vieta',
        };
        return labels[level];
    };

    const getPlaceLevelColor = (level: CompetitionLevel): string => {
        const colors: Record<CompetitionLevel, string> = {
            I: 'gold',
            II: 'silver',
            III: 'bronze',
            SPECIAL: 'purple',
        };
        return colors[level];
    };

    const getSchoolTypeLabel = (type: string): string => {
        const schoolType = schoolTypeOptions.find(option => option.value === type);
        return schoolType ? schoolType.label : type;
    };

    const getSchoolColor = (type: string): string => {
        const schoolType = schoolTypeOptions.find(option => option.value === type);
        return schoolType ? schoolType.color : '#8c8c8c';
    };

    const handleSchoolClick = (school: SchoolWithStatistics) => {
        setSelectedSchool(school);
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
    };

    const handleTypeChange = (value: string) => {
        setSelectedSchoolType(value);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedSchoolType('');
    };

    // School winners table columns
    const schoolWinnersColumns = [
        {
            title: 'Konkursų tipas',
            dataIndex: 'type',
            key: 'type',
            render: (type: CompetitionType) => getCompetitionTypeLabel(type),
        },
        {
            title: 'Viso nugalėtojų',
            dataIndex: 'total_winners',
            key: 'total_winners',
            sorter: (a: any, b: any) => a.total_winners - b.total_winners,
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'I vieta',
            dataIndex: 'first_place',
            key: 'first_place',
            render: (value: number) => <Tag color="gold">{value}</Tag>,
        },
        {
            title: 'II vieta',
            dataIndex: 'second_place',
            key: 'second_place',
            render: (value: number) => <Tag color="silver">{value}</Tag>,
        },
        {
            title: 'III vieta',
            dataIndex: 'third_place',
            key: 'third_place',
            render: (value: number) => <Tag color="bronze">{value}</Tag>,
        },
        {
            title: 'Speciali vieta',
            dataIndex: 'special_place',
            key: 'special_place',
            render: (value: number) => <Tag color="purple">{value}</Tag>,
        },
    ];

    const topSchoolsColumns = [
        {
            title: 'Mokykla',
            dataIndex: 'school_name',
            key: 'school_name',
            render: (name: string, record: any) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        style={{ backgroundColor: '#fdab01', marginRight: '12px' }}
                        icon={<BankOutlined />} />
                    {name}
                </div>
            ),
        },
        {
            title: 'Viso nugalėtojų',
            dataIndex: 'total_winners',
            key: 'total_winners',
            sorter: (a: any, b: any) => a.total_winners - b.total_winners,
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'I vieta',
            dataIndex: 'first_place_winners',
            key: 'first_place_winners',
            render: (value: number) => <Tag color="gold">{value}</Tag>,
        },
        {
            title: 'II vieta',
            dataIndex: 'second_place_winners',
            key: 'second_place_winners',
            render: (value: number) => <Tag color="silver">{value}</Tag>,
        },
        {
            title: 'III vieta',
            dataIndex: 'third_place_winners',
            key: 'third_place_winners',
            render: (value: number) => <Tag color="bronze">{value}</Tag>,
        },
        {
            title: 'Speciali vieta',
            dataIndex: 'special_place_winners',
            key: 'special_place_winners',
            render: (value: number) => <Tag color="purple">{value}</Tag>,
        },
        {
            title: 'Konkursų',
            dataIndex: 'competitions_participated',
            key: 'competitions_participated',
        },
    ];

    const renderSchoolDetails = () => {
        if (!selectedSchool) {
            return (
                <Card>
                    <div style={{ textAlign: 'center', padding: '50px', color: '#8c8c8c' }}>
                        <TrophyOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                        <div>Pasirinkite mokyklą, kad pamatytumėte detalią informaciją</div>
                    </div>
                </Card>
            );
        }

        const { statistics, winners_by_competition_type } = selectedSchool;
        const winnersData = Object.entries(winners_by_competition_type).map(([type, data]) => ({
            type: type as CompetitionType,
            ...data,
        }));

        const schoolIcon: any = { icon: <BankOutlined />, color: getSchoolColor(selectedSchool.type) };

        return (
            <div>
                {/* School Header */}
                <Card style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                        <Avatar
                            icon={schoolIcon.icon}
                            style={{ backgroundColor: schoolIcon.color, marginRight: '12px' }}
                            size={48} />
                        <div>
                            <Title level={3} style={{ margin: 0 }}>{selectedSchool.name}</Title>
                            <Text type="secondary">{getSchoolTypeLabel(selectedSchool.type)}</Text>
                        </div>
                    </div>
                </Card>

                {/* School Statistics */}
                <Card title="Bendra mokyklos statistika" style={{ marginBottom: '16px' }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={12} sm={6}>
                            <Statistic
                                title="Viso nugalėtojų"
                                value={statistics.total_winners}
                                prefix={<TrophyOutlined style={{ color: '#faad14' }} />} />
                        </Col>
                        <Col xs={12} sm={6}>
                            <Statistic
                                title="Dalyvavo konkursų"
                                value={statistics.competitions_participated}
                                prefix={<BookOutlined />} />
                        </Col>
                        <Col xs={12} sm={6}>
                            <Statistic
                                title="I vietos"
                                value={statistics.first_place_winners}
                                valueStyle={{ color: '#faad14' }} />
                        </Col>
                        <Col xs={12} sm={6}>
                            <Statistic
                                title="II vietos"
                                value={statistics.second_place_winners}
                                valueStyle={{ color: '#8c8c8c' }} />
                        </Col>
                        <Col xs={12} sm={6}>
                            <Statistic
                                title="III vietos"
                                value={statistics.third_place_winners}
                                valueStyle={{ color: '#d48806' }} />
                        </Col>
                        <Col xs={12} sm={6}>
                            <Statistic
                                title="Specialios vietos"
                                value={statistics.special_place_winners}
                                valueStyle={{ color: '#722ed1' }} />
                        </Col>
                    </Row>
                </Card>

                {/* Winners by Competition Type */}
                <Card title="Nugalėtojai pagal konkursų tipus">
                    <Table
                        columns={schoolWinnersColumns}
                        dataSource={winnersData}
                        rowKey="type"
                        pagination={false}
                        size="small" />
                </Card>
            </div>
        );
    };

    return (
        <div className="competition-statistics" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <Title level={2} style={{ margin: 0 }}>Konkursų statistika</Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <RangePicker
                        value={dateRange}
                        onChange={handleDateChange as any}
                        format="YYYY-MM-DD"
                        placeholder={['Pradžios data', 'Pabaigos data']}
                        allowClear={false} />
                    <Tooltip title="Perkrauti duomenis">
                        <Button icon={<ReloadOutlined />} onClick={handleRefresh} />
                    </Tooltip>
                </div>
            </div>

            {/* Overall Statistics */}
            <Card title={`Bendra statistika: ${date_from} - ${date_to}`} className="statistics-card overall-stats" style={{ marginBottom: '24px' }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Statistic
                            title="Viso konkursų"
                            value={overall_statistics.total_competitions}
                            prefix={<TrophyOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Statistic
                            title="Viso dalyvių"
                            value={overall_statistics.total_participants}
                            prefix={<TeamOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Statistic
                            title="Viso nugalėtojų"
                            value={overall_statistics.total_winners}
                            prefix={<TrophyOutlined style={{ color: '#faad14' }} />} />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Statistic
                            title="Mokyklų su nugalėtojais"
                            value={overall_statistics.schools_with_winners}
                            prefix={<ShopOutlined />} />
                    </Col>
                </Row>
                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                    <Col xs={24} sm={12} md={6}>
                        <Statistic
                            title="Vidutiniškai dalyvių/konkursui"
                            value={overall_statistics.average_participants_per_competition}
                            precision={1} />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Statistic
                            title="Vidutiniškai nugalėtojų/konkursui"
                            value={overall_statistics.average_winners_per_competition}
                            precision={1} />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Statistic
                            title="Nugalėtojų procentas"
                            value={overall_statistics.winner_percentage}
                            suffix="%"
                            precision={1} />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Statistic
                            title="Dalyvaujančių mokytojų"
                            value={overall_statistics.total_teachers_involved}
                            prefix={<UserOutlined />} />
                    </Col>
                </Row>
            </Card>

            {/* Top Schools */}
            <Card title="Geriausios mokyklos" className="statistics-card table-section" style={{ marginBottom: '24px' }}>
                <Table
                    columns={topSchoolsColumns}
                    dataSource={top_schools}
                    rowKey="id"
                    pagination={false}
                    size="small" />
            </Card>

            {/* Competition Types Statistics */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} lg={12}>
                    <Card title="Konkursų statistika pagal tipą" className="statistics-card progress-section">
                        {Object.entries(competition_statistics_by_type).map(([type, stats]) => (
                            <div key={type} style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <Text strong>{getCompetitionTypeLabel(type as CompetitionType)}</Text>
                                    <Text>{stats.total_competitions} konkursai</Text>
                                </div>
                                <Progress
                                    percent={Math.round((stats.total_participants / overall_statistics.total_participants) * 100)}
                                    format={(percent) => `${stats.total_participants} dalyvių (${percent}%)`}
                                    strokeColor="#fdab01" />
                            </div>
                        ))}
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="Nugalėtojų statistika pagal konkursų tipą" className="statistics-card progress-section">
                        {Object.entries(winners_statistics_by_type).map(([type, stats]) => (
                            <div key={type} style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <Text strong>{getCompetitionTypeLabel(type as CompetitionType)}</Text>
                                    <Text>{stats.total_winners} nugalėtojų</Text>
                                </div>
                                <Progress
                                    percent={Math.round((stats.total_winners / overall_statistics.total_winners) * 100)}
                                    format={(percent) => `${stats.competitions_with_winners} konkursai (${percent}%)`}
                                    strokeColor="#52c41a" />
                            </div>
                        ))}
                    </Card>
                </Col>
            </Row>

            {/* Winners by Place Level */}
            <Card title="Nugalėtojai pagal vietas" className="statistics-card place-level-stats" style={{ marginBottom: '24px' }}>
                <Row gutter={[16, 16]}>
                    {Object.entries(winners_by_place_level).map(([level, stats]) => (
                        <Col xs={12} sm={6} key={level}>
                            <Card size="small" style={{ textAlign: 'center' }}>
                                <Statistic
                                    title={getPlaceLevelLabel(level as CompetitionLevel)}
                                    value={stats.total_winners}
                                    valueStyle={{ color: getPlaceLevelColor(level as CompetitionLevel) }} />
                                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                    {stats.competitions} konkursai, {stats.schools} mokyklos
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Card>

            {/* Schools List and Details */}
            <Row gutter={[24, 24]}>
                {/* Left side - Schools list */}
                <Col xs={24} lg={8}>
                    <Card
                        title={`Mokyklos (${filteredSchools.length}${selectedSchoolType ? ` - ${getSchoolTypeLabel(selectedSchoolType)}` : ''}${searchTerm ? ` - paieška: "${searchTerm}"` : ''})`}
                        className="statistics-card">
                        {/* Filters */}
                        <div style={{ marginBottom: '16px' }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Search
                                    placeholder="Ieškoti mokyklos..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    prefix={<SearchOutlined />}
                                    allowClear />
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <Select
                                        placeholder="Mokyklos tipas"
                                        value={selectedSchoolType}
                                        onChange={handleTypeChange}
                                        allowClear
                                        style={{ flex: 1 }}>
                                        {schoolTypeOptions.map(option => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                    {(searchTerm || selectedSchoolType) && (
                                        <button
                                            type="button"
                                            onClick={handleClearFilters}
                                            style={{
                                                padding: '4px 8px',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '6px',
                                                background: 'white',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                            }}>
                                            Išvalyti
                                        </button>
                                    )}
                                </div>
                            </Space>
                        </div>

                        {/* Schools List */}
                        <List
                            dataSource={filteredSchools}
                            renderItem={(school) => {
                                const schoolIcon: any = { icon: <BankOutlined />, color: getSchoolColor(school.type) };
                                return (
                                    <List.Item
                                        onClick={() => handleSchoolClick(school)}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: selectedSchool?.id === school.id ? '#f0f0f0' : 'transparent',
                                            padding: '12px',
                                            borderRadius: '6px',
                                            transition: 'background-color 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = selectedSchool?.id === school.id ? '#f0f0f0' : '#fafafa';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = selectedSchool?.id === school.id ? '#f0f0f0' : 'transparent';
                                        }}>
                                        <List.Item.Meta
                                            avatar={(
                                                <Avatar
                                                    icon={schoolIcon.icon}
                                                    style={{ backgroundColor: schoolIcon.color }} />
                                            )}
                                            title={(
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Text strong style={{ color: '#fdab01' }}>
                                                        {school.name}
                                                    </Text>
                                                    <Tag color={school.statistics.total_winners > 0 ? 'green' : 'default'}>
                                                        {school.statistics.total_winners} nugalėtojų
                                                    </Tag>
                                                </div>
                                            )}
                                            description={(
                                                <div>
                                                    <div>
                                                        <Text type="secondary">
                                                            {getSchoolTypeLabel(school.type)}
                                                        </Text>
                                                    </div>
                                                    <div style={{ marginTop: '4px' }}>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            Dalyvavo {school.statistics.competitions_participated} konkursuose
                                                        </Text>
                                                    </div>
                                                </div>
                                            )} />
                                    </List.Item>
                                );
                            }} />
                    </Card>
                </Col>

                {/* Right side - School Details */}
                <Col xs={24} lg={16}>
                    {renderSchoolDetails()}
                </Col>
            </Row>

            <div className="report-timestamp">
                <Text type="secondary">
                    Ataskaita sugeneruota: {new Date(reports.report_generated_at).toLocaleString('lt-LT')}
                </Text>
            </div>
        </div>
    );
};

export default CompetitionStatistics;
